import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Lead } from '@/types/lead';
import BusinessOverview from '@/components/business/BusinessOverview';
import TaskPanel from '../tasks/panel/TaskPanel';
import WebSearchPanel from '@/components/WebSearchPanel';
import { LinkedInDataView } from './LinkedInDataView';
import { getWebSearchData, getWebSearchStatus, enrichLeadWithWebSearch } from '@/services/webSearchService';
import DataQualityCard from '@/components/websearch/DataQualityCard';
import { supabase } from '@/integrations/supabase/client';
import { useAppSettings } from '@/hooks/useAppSettings';

interface OverviewTabContentProps {
  lead: Lead;
  onLeadUpdated?: () => void;
}

const OverviewTabContent: React.FC<OverviewTabContentProps> = ({ lead, onLeadUpdated }) => {
  const { showLinkedInSection } = useAppSettings();
  const [linkedIn, setLinkedIn] = useState<any>(lead.webSearchData?.linkedIn || null);
  const [dataQuality, setDataQuality] = useState<any>(lead.webSearchData?.dataQuality || null);
  const [dataQualityHistory, setDataQualityHistory] = useState<any[]>(lead.webSearchData?.dataQualityHistory || []);
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichmentStatus, setEnrichmentStatus] = useState<string | null>(
    lead.webSearchData?.status || null
  );
  const [queuePosition, setQueuePosition] = useState<number | null>(null);
  const hasRetriedRef = useRef(false);
  const retryCountRef = useRef(0);
  const lastRetryAtRef = useRef<number | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isEnrichingRef = useRef(false);
  const pollingStartTimeRef = useRef<number | null>(null);
  const currentPollingIntervalRef = useRef<number>(10000); // Start with 10s

  const loadLatest = async () => {
    try {
      const data = await getWebSearchData(lead.id);
      if (data) {
        if (data.linkedIn) {
          setLinkedIn(data.linkedIn);
        }
        if (data.dataQuality) {
          setDataQuality(data.dataQuality);
        }
        if (data.dataQualityHistory) {
          setDataQualityHistory(data.dataQualityHistory);
        }
        setIsEnriching(false);
      }
    } catch (e) {
      console.error('Failed to load latest data', e);
      setIsEnriching(false);
    }
  };

  const handleEnrichmentStatusChange = useCallback((status: string | null, processing: boolean, position: number | null) => {
    setEnrichmentStatus(status);
    setIsEnriching(processing || status === 'pending' || status === 'processing');
    setQueuePosition(position ?? null);
  }, []);

  useEffect(() => {
    // Initialize from lead prop - use existing data if available
    const existingLinkedIn = lead.webSearchData?.linkedIn;
    setLinkedIn(existingLinkedIn || null);
    
    const initializeStatus = async () => {
      // Only show loading if we don't have data yet
      const hasExistingData = existingLinkedIn && 
        (existingLinkedIn as any).dataSource === 'verified_linkedin';
      
      if (!hasExistingData) {
        setIsEnriching(true);
      }
      
      const status = await getWebSearchStatus(lead.id);
      if (status?.status) {
        setEnrichmentStatus(status.status);
        
        // Only set enriching if actually pending/processing AND no verified data
        if ((status.status === 'pending' || status.status === 'processing') && !hasExistingData) {
          setIsEnriching(true);
        } else {
          setIsEnriching(false);
        }
      } else {
        setIsEnriching(false);
      }
      
      // Only fetch latest if we don't have verified data
      if (!hasExistingData) {
        await loadLatest();
      }
    };
    
    initializeStatus();
  }, [lead.id]);

  // Real-time Supabase subscription for LinkedIn data updates
  useEffect(() => {
    const channel = supabase
      .channel(`lead-linkedin-${lead.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'leads',
          filter: `id=eq.${lead.id}`
        },
        (payload) => {
          console.log('Lead updated via realtime:', { id: payload.new?.id, web_search_status: payload.new?.web_search_status, crawl_status: payload.new?.crawl_status });
          
          // Update LinkedIn data if changed
          if (payload.new.web_search_data?.linkedIn) {
            const newLinkedIn = payload.new.web_search_data.linkedIn;
            const isVerified = newLinkedIn.dataSource === 'verified_linkedin';
            
            // Always update if verified, or if we don't have data yet
            if (isVerified || !linkedIn) {
              setLinkedIn(newLinkedIn);
            }
          }
          
          // Update status
          if (payload.new.web_search_status) {
            const newStatus = payload.new.web_search_status;
            setEnrichmentStatus(newStatus);
            
            // Stop enriching state when completed or failed
            if (newStatus === 'completed' || newStatus === 'failed') {
              setIsEnriching(false);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [lead.id, linkedIn]);

  // Polling fallback for enrichment status with exponential backoff
  useEffect(() => {
    // Clear any existing interval first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Only poll if actively processing (not pending) AND we don't have verified data
    const hasVerifiedData = linkedIn && (linkedIn as any).dataSource === 'verified_linkedin';
    const needsPolling = enrichmentStatus === 'processing' && !hasVerifiedData;

    if (needsPolling) {
      // Initialize polling start time and reset interval
      if (!pollingStartTimeRef.current) {
        pollingStartTimeRef.current = Date.now();
        currentPollingIntervalRef.current = 10000; // Reset to 10s
      }

      const pollStatus = async () => {
        // Check if we've been polling for more than 10 minutes
        const elapsedTime = Date.now() - (pollingStartTimeRef.current || 0);
        const maxPollingDuration = 10 * 60 * 1000; // 10 minutes

        if (elapsedTime > maxPollingDuration) {
          console.log('[OverviewTab] Max polling duration reached, stopping polling');
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsEnriching(false);
          return;
        }

        const status = await getWebSearchStatus(lead.id);
        
        if (status?.status === 'completed') {
          await loadLatest();
          setEnrichmentStatus('completed');
          setIsEnriching(false);
          pollingStartTimeRef.current = null;
          
          // Clear interval on completion
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        } else if (status?.status === 'failed') {
          setEnrichmentStatus('failed');
          setIsEnriching(false);
          pollingStartTimeRef.current = null;
          
          // Clear interval on failure
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        } else {
          // Implement exponential backoff: 10s -> 20s -> 40s -> 60s (max)
          const maxInterval = 60000; // 60 seconds max
          currentPollingIntervalRef.current = Math.min(
            currentPollingIntervalRef.current * 2,
            maxInterval
          );
          
          console.log(`[OverviewTab] Next poll in ${currentPollingIntervalRef.current / 1000}s`);
          
          // Clear current interval and set new one with updated interval
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          
          pollingIntervalRef.current = setInterval(pollStatus, currentPollingIntervalRef.current) as any;
        }
      };

      // Start polling with initial interval
      pollingIntervalRef.current = setInterval(pollStatus, currentPollingIntervalRef.current) as any;

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    } else {
      // Reset polling state when not polling
      pollingStartTimeRef.current = null;
      currentPollingIntervalRef.current = 10000;
    }
  }, [enrichmentStatus, lead.id, linkedIn]);

  return (
    <div className="space-y-4">
      <BusinessOverview lead={lead} onLeadUpdated={onLeadUpdated} />
      {showLinkedInSection && (
        <LinkedInDataView 
          data={linkedIn} 
          leadId={lead.id} 
          onVerify={loadLatest}
        />
      )}
      <TaskPanel leadId={lead.id} />
      <DataQualityCard
        dataQuality={dataQuality}
        dataQualityHistory={dataQualityHistory}
        keyContacts={lead.keyContacts}
      />
      <WebSearchPanel 
        lead={lead} 
        onDataUpdated={loadLatest}
        onEnrichmentStatusChange={handleEnrichmentStatusChange}
      />
    </div>
  );
};

export default OverviewTabContent;
